import { itemTypeName, type Game, type ItemType } from '@hive/shared';
import { Link2, Paperclip, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/field';
import { useGames } from '@/data/games';
import { queryKeys } from '@/data/keys';
import { useRealtimeInvalidation } from '@/data/useRealtimeInvalidation';
import {
  useAddVaultLink,
  useFiles,
  useItemLinks,
  useLinkItems,
  useSearch,
  useUnlink,
  useVaultLinks,
  type ItemRef,
  type LinkedItem,
  type SearchHit,
  type VaultLinkRow,
} from '@/data/vault';
import { cn } from '@/lib/utils';
import { FileView, Thumb } from './cards';
import { linkTitle, pathFor } from './model';
import { useLinkThumbnails } from './useLinkThumbnails';
import { useOpenUpload } from './upload-context';

/**
 * What an item points to and what points to it. Attachments (vault links and
 * files attached to it) show as cards; everything else as "Links to" and
 * "Linked from". Link… searches everything.
 */
export function LinksPanel({ item, className }: { item: ItemRef; className?: string }) {
  const links = useItemLinks(item);
  useRealtimeInvalidation('links', queryKeys.itemLinks(item.type, item.id));
  const games = useGames();
  const unlink = useUnlink();
  const openUpload = useOpenUpload();
  const fileInput = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState(false);
  const [pasting, setPasting] = useState(false);

  const to = links.data?.to ?? [];
  const attachments = to.filter((l) => l.kind === 'attached' && (l.type === 'vault_link' || l.type === 'file'));
  const linksTo = to.filter((l) => !attachments.includes(l));
  const linkedFrom = links.data?.from ?? [];
  const gameList = games.data ?? [];

  return (
    <section aria-label="Links" className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto font-display text-[9px] font-bold tracking-[0.18em] text-muted uppercase">Links</h3>
        <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
          <Link2 aria-hidden /> Link…
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setPasting((p) => !p)} aria-pressed={pasting}>
          <Paperclip aria-hidden /> Attach link
        </Button>
        {openUpload ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => fileInput.current?.click()}>
              <Upload aria-hidden /> Upload
            </Button>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              aria-label="Choose files to upload"
              onChange={(e) => {
                openUpload([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
          </>
        ) : null}
      </div>
      {pasting ? <AttachUrl item={item} onDone={() => setPasting(false)} /> : null}

      {attachments.length > 0 ? (
        <ul aria-label="Attachments" className="flex flex-col gap-2">
          {attachments.map((a) => (
            <Attachment key={a.linkId} item={a} games={gameList} onRemove={() => unlink.mutate(a.linkId)} />
          ))}
        </ul>
      ) : null}

      <LinkList label="Links to" items={linksTo} games={gameList} onRemove={(l) => unlink.mutate(l.linkId)} />
      <LinkList label="Linked from" items={linkedFrom} games={gameList} onRemove={(l) => unlink.mutate(l.linkId)} />
      {links.isPending ? <p className="text-xs text-dim">Loading links</p> : null}
      {!links.isPending && to.length === 0 && linkedFrom.length === 0 ? (
        <p className="text-xs text-dim">
          Nothing linked yet.{openUpload ? ' Drop or paste a file here to attach it.' : ''}
        </p>
      ) : null}

      {picking ? <ItemPicker from={item} onClose={() => setPicking(false)} /> : null}
    </section>
  );
}

function LinkList({
  label,
  items,
  games,
  onRemove,
}: {
  label: string;
  items: readonly LinkedItem[];
  games: readonly Game[];
  onRemove: (item: LinkedItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <h4 className="font-mono text-[11px] text-muted">{label}</h4>
      <ul aria-label={label} className="flex flex-col gap-px">
        {items.map((l) => (
          <li key={l.linkId} className="flex items-center gap-2 bg-bg-3/60 px-2 py-1.5">
            <span className="w-12 shrink-0 font-mono text-[10px] text-magenta uppercase">{itemTypeName(l.type)}</span>
            {l.missing ? (
              <span className="flex-1 truncate text-sm text-dim italic">{l.title}</span>
            ) : (
              <RouterLink to={pathFor(l, games)} className="flex-1 truncate text-sm hover:text-cyan">
                {l.title}
              </RouterLink>
            )}
            {l.kind !== 'relates' ? <span className="font-mono text-[10px] text-dim">{l.kind}</span> : null}
            <button
              type="button"
              className="cursor-pointer p-0.5 text-muted hover:text-red"
              aria-label={`Remove link to ${l.title}`}
              onClick={() => onRemove(l)}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Attachment({ item, games, onRemove }: { item: LinkedItem; games: readonly Game[]; onRemove: () => void }) {
  const links = useVaultLinks();
  const files = useFiles();
  const link = item.type === 'vault_link' ? links.data?.find((l) => l.id === item.id) : undefined;
  const file = item.type === 'file' ? files.data?.find((f) => f.id === item.id) : undefined;
  return (
    <li className="flex items-start gap-2 bg-bg-3/60 p-2">
      <div className="min-w-0 flex-1">
        {file ? <FileView file={file} small /> : null}
        {link ? (
          <div className="flex items-center gap-2.5">
            <LinkThumb link={link} />
            <div className="min-w-0">
              <a href={link.url} target="_blank" rel="noreferrer" className="block truncate text-sm hover:text-cyan">
                {linkTitle(link)}
              </a>
              <RouterLink to={pathFor(item, games)} className="font-mono text-[11px] text-muted hover:text-cyan">
                Details in Vault
              </RouterLink>
            </div>
          </div>
        ) : null}
        {!file && !link ? <span className="text-sm text-dim">{item.title}</span> : null}
      </div>
      <button
        type="button"
        className="cursor-pointer p-0.5 text-muted hover:text-red"
        aria-label={`Detach ${item.title}`}
        onClick={onRemove}
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </li>
  );
}

function LinkThumb({ link }: { link: VaultLinkRow }) {
  const thumbs = useLinkThumbnails(link);
  return <Thumb srcs={thumbs} kind={link.kind} className="h-12 w-20 shrink-0 [&_svg]:size-5" />;
}

function AttachUrl({ item, onDone }: { item: ItemRef; onDone: () => void }) {
  const add = useAddVaultLink();
  const link = useLinkItems();
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const busy = add.isPending || link.isPending;

  async function submit() {
    setError(null);
    try {
      const saved = await add.mutateAsync({ url });
      await link.mutateAsync({ from: item, to: { type: 'vault_link', id: saved.id }, kind: 'attached' });
      setUrl('');
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t attach that link.');
    }
  }

  return (
    <form
      className="flex flex-col gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (url.trim()) void submit();
      }}
    >
      <div className="flex gap-2">
        <Input
          autoFocus
          aria-label="Link to attach"
          placeholder="Paste a Drive, Figma or web link"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Button size="sm" type="submit" disabled={busy || !url.trim()}>
          {busy ? 'Saving…' : 'Attach'}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="font-mono text-[11px] text-red">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/** Search across Hive and link the item to what you pick. */
export function ItemPicker({ from, onClose }: { from: ItemRef; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const results = useSearch(query, { limit: 20 });
  const link = useLinkItems();
  const hits = (results.data ?? []).filter((h) => !(h.item_type === from.type && h.item_id === from.id));

  const pick = (hit: SearchHit) =>
    link.mutate({ from, to: { type: hit.item_type, id: hit.item_id } }, { onSuccess: onClose });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title="Link to…" aria-describedby={undefined}>
        <Input
          autoFocus
          aria-label="Search Hive"
          placeholder="Search tasks, ideas, docs, links and files"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ul aria-label="Results" className="flex flex-col gap-px">
          {hits.map((h) => (
            <li key={`${h.item_type}:${h.item_id}`}>
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 bg-bg-3/60 px-2 py-1.5 text-left hover:bg-cyan/10"
                onClick={() => pick(h)}
                disabled={link.isPending}
              >
                <span className="w-12 shrink-0 font-mono text-[10px] text-magenta uppercase">
                  {itemTypeName(h.item_type as ItemType)}
                </span>
                <span className="flex-1 truncate text-sm">{h.title}</span>
              </button>
            </li>
          ))}
        </ul>
        {query.trim() && results.isSuccess && hits.length === 0 ? (
          <p className="text-sm text-dim">Nothing matches.</p>
        ) : null}
        {link.error ? (
          <p role="alert" className="font-mono text-[11px] text-red">
            {link.error.message}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
